import { useMemo, useState } from 'react';
import type { Movimiento } from '../../lib/supabase';
import { MOTIVOS_OBJECION, type ObjecionComprobante } from '../../types/comprobante';
import {
  candidatosDeObjecion, MOTIVO_OTRO, objecionesDeMarcas, type CandidatoObjecion, type MarcaObjecion,
} from '../../utils/objecionesComprobantes';
import { formatCurrency, formatDate } from '../../utils/formatters';
import { Checkbox } from '../ui/Checkbox';
import { Select } from '../ui/Select';
import { TextInput } from '../ui/TextInput';

interface ObjecionesComprobantesProps {
  /** Movimientos del mes, con sus comprobantes embebidos. */
  readonly movimientos: readonly Movimiento[];
  readonly onChange: (objeciones: readonly ObjecionComprobante[]) => void;
}

type Marcas = Readonly<Record<string, MarcaObjecion>>;

function descripcionDelCandidato({ movimiento, respaldo }: CandidatoObjecion): string {
  const sufijo = respaldo === 'reembolso' ? ' (reembolso)' : '';
  return `${formatDate(movimiento.fecha)} · ${movimiento.descripcion ?? 'Sin descripción'} · ${formatCurrency(Number(movimiento.monto))}${sufijo}`;
}

interface FilaObjecionProps {
  readonly candidato: CandidatoObjecion;
  readonly marca: MarcaObjecion | undefined;
  readonly onAlternar: (marcado: boolean) => void;
  readonly onCambiar: (marca: MarcaObjecion) => void;
}

const FilaObjecion: React.FC<FilaObjecionProps> = ({ candidato, marca, onAlternar, onCambiar }) => (
  <li className="rounded-xl border border-slate-200 p-3 space-y-2">
    <Checkbox
      id={`objetar-${candidato.clave}`}
      label={descripcionDelCandidato(candidato)}
      checked={marca !== undefined}
      onChange={onAlternar}
    />
    {marca && (
      <Select
        label="Motivo"
        options={MOTIVOS_OBJECION.map((m) => ({ value: m, label: m }))}
        value={marca.motivo}
        onChange={(motivo) => { onCambiar({ ...marca, motivo }); }}
      />
    )}
    {marca?.motivo === MOTIVO_OTRO && (
      <TextInput label="¿Cuál?" value={marca.otro} required onChange={(otro) => { onCambiar({ ...marca, otro }); }} />
    )}
  </li>
);

/** Quien revisa un balance puede objetar comprobantes puntuales con su motivo; no aparece si no hay ninguno que objetar. */
export const ObjecionesComprobantes: React.FC<ObjecionesComprobantesProps> = ({ movimientos, onChange }) => {
  const [marcas, setMarcas] = useState<Marcas>({});
  const candidatos = useMemo(() => candidatosDeObjecion(movimientos), [movimientos]);

  const publicar = (nuevas: Marcas) => {
    setMarcas(nuevas);
    onChange(objecionesDeMarcas(candidatos, nuevas));
  };

  const alternar = (clave: string, marcado: boolean) => {
    if (marcado) {
      publicar({ ...marcas, [clave]: { motivo: MOTIVOS_OBJECION[0], otro: '' } });
      return;
    }
    publicar(Object.fromEntries(Object.entries(marcas).filter(([k]) => k !== clave)));
  };

  if (candidatos.length === 0) return null;

  return (
    <section className="space-y-3">
      <div>
        <h3 className="text-sm font-semibold text-slate-700">Objetar comprobantes (opcional)</h3>
        <p className="text-xs text-slate-500">
          El movimiento objetado vuelve a pendiente con el motivo a la vista, y el balance no se podrá reenviar hasta que se corrija.
        </p>
      </div>
      <ul className="space-y-3">
        {candidatos.map((c) => (
          <FilaObjecion
            key={c.clave}
            candidato={c}
            marca={marcas[c.clave]}
            onAlternar={(marcado) => { alternar(c.clave, marcado); }}
            onCambiar={(marca) => { publicar({ ...marcas, [c.clave]: marca }); }}
          />
        ))}
      </ul>
    </section>
  );
};
